// @ts-strict-ignore
import { ProductListQuery } from "@dashboard/graphql";
import { RelayToFlat } from "@dashboard/types";
import { Box, ProductsIcons, Text } from "@saleor/macaw-ui-next";
import React from "react";

export interface ProductTileProps {
  product: RelayToFlat<ProductListQuery["products"]>[0];
  onClick: () => void;
  selectedChannelId?: string;
}

export const ProductTile = ({ product, onClick, selectedChannelId }: ProductTileProps) => {
  // Get the channel listing for the selected channel or the first one
  const channel = product?.channelListings?.find(
    listing => listing.channel.id === selectedChannelId,
  ) ?? product?.channelListings?.[0];

  // Use channel.isPublished to determine if product is published or draft
  const isPublished = channel?.isPublished ?? false;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      onClick={onClick}
      cursor="pointer"
      borderRadius={4}
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      backgroundColor="default1"
      overflow="hidden"
      data-test-id={`product-tile-${product.id}`}
    >
      {/* Product Image */}
      {product.thumbnail ? (
        <Box
          as="img"
          alt={product.name}
          src={product.thumbnail.url}
          __width="100%"
          __height="280px"
          __objectFit="cover"
          backgroundColor="default2"
        />
      ) : (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          backgroundColor="default2"
          __width="100%"
          __height="280px"
        >
          <Box __width="40%" __height="40%">
            <ProductsIcons
              size="fill"
              color="defaultDisabled"
              data-test-id={`placeholder-svg-${product.id}`}
            />
          </Box>
        </Box>
      )}

      {/* Product Info */}
      <Box padding={4} display="flex" flexDirection="column" gap={2}>
        {/* Status Row */}
        <Box display="flex" justifyContent="flex-start" alignItems="center">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingTop: "4px",
              paddingBottom: "4px",
              borderRadius: "6px",
              backgroundColor: isPublished
                ? "rgba(59, 130, 246, 0.15)"
                : "rgba(251, 191, 36, 0.25)",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: isPublished
                  ? "rgb(59, 130, 246)"
                  : "rgb(251, 191, 36)",
              }}
            />
            <Text
              size={2}
              fontWeight="medium"
              style={{
                color: isPublished
                  ? "rgb(59, 130, 246)"
                  : "rgb(251, 191, 36)",
              }}
            >
              {isPublished ? "Published" : "Draft"}
            </Text>
          </div>
        </Box>

        {/* Product Name */}
        <Text size={4} fontWeight="medium" ellipsis>
          {product.name}
        </Text>

        {/* Category */}
        {product.category && (
          <Text size={2} color="default2">
            {product.category.name}
          </Text>
        )}

        {/* Date Row */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" marginTop={2}>
          {product.updatedAt && (
            <Text size={2} color="default2">
              Updated {formatDate(product.updatedAt)}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
